/**
 * Login screen — email + password form → session.login → home.
 *
 * Displays the error envelope message on failure (ApiRequestError.message is already the
 * human-readable string from the backend error envelope).
 *
 * WO-M0 — built on the design system: Screen/Field/Button/Text + the BeamLogo brand.
 * Web portal polish: on wide web viewports this becomes a two-pane portal (brand panel +
 * sign-in card) and links back to the getabeam.com marketing site for sign-up/downloads.
 * On phones (and native) it keeps the original centered single-column form.
 */

import { useState } from 'react';
import { KeyboardAvoidingView, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSession } from '../src/auth/session';
import { ApiRequestError } from '../src/api/client';
import { Screen, Text, Field, Button, Card } from '../src/components/ui';
import { BeamLogo, BeamMark } from '../src/components/brand/Logo';
import { useResponsive } from '../src/hooks/useResponsive';
import { theme } from '../src/theme';

const IS_WEB = Platform.OS === 'web';
const PORTAL_BREAKPOINT = 920;

const VALUE_PROPS = [
  'See every estimate, invoice, and job in one place',
  'Run P&L, exports, and printing from the big screen',
  'Stay in sync with the crew in the field',
];

export default function LoginScreen() {
  const login = useSession((s) => s.login);
  const router = useRouter();
  const { width } = useResponsive();
  const twoPane = IS_WEB && width >= PORTAL_BREAKPOINT;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleLogin() {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError('Email and password are required.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await login(trimmedEmail, password);
      // Navigation handled by the AuthGate in _layout.tsx once status → 'authenticated'.
    } catch (err) {
      if (err instanceof ApiRequestError || err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  const form = (
    <View style={styles.form}>
      <Field
        label="Email"
        value={email}
        onChangeText={setEmail}
        autoCapitalize="none"
        autoCorrect={false}
        keyboardType="email-address"
        textContentType="emailAddress"
        autoComplete="email"
        placeholder="you@example.com"
        editable={!loading}
        accessibilityLabel="Email address"
        returnKeyType="next"
      />
      <Field
        label="Password"
        value={password}
        onChangeText={setPassword}
        secureToggle
        textContentType="password"
        autoComplete="password"
        placeholder="Password"
        editable={!loading}
        accessibilityLabel="Password"
        returnKeyType="done"
        onSubmitEditing={handleLogin}
        error={error ?? undefined}
      />
      <Pressable accessibilityRole="link" testID="login-forgot-password" style={styles.forgotRow} onPress={() => router.push('/forgot-password')}>
        <Text variant="small" color="link">
          Forgot password?
        </Text>
      </Pressable>
      <Button title="Sign in" onPress={handleLogin} loading={loading} disabled={loading} size="lg" style={styles.submit} />

      {IS_WEB ? (
        <View style={styles.altRow}>
          <Text variant="small" color="secondary">
            New to Beam?
          </Text>
          {/* Web account creation goes through the marketing Stripe flow, never the native paywall. */}
          <Pressable accessibilityRole="link" onPress={() => router.push('/pricing')}>
            <Text variant="small" color="link">
              See pricing
            </Text>
          </Pressable>
          <Text variant="small" color="secondary">
            ·
          </Text>
          <Pressable accessibilityRole="link" onPress={() => router.push('/download')}>
            <Text variant="small" color="link">
              Download the app
            </Text>
          </Pressable>
        </View>
      ) : (
        <View style={styles.altRow}>
          <Text variant="small" color="secondary">
            New to Beam?
          </Text>
          {/* Native account creation → in-app signup → store paywall. */}
          <Pressable accessibilityRole="link" testID="login-create-account" onPress={() => router.push('/signup')}>
            <Text variant="small" color="link">
              Create an account
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );

  // --- Wide web: two-pane portal -----------------------------------------------------------
  if (twoPane) {
    return (
      <Screen padded={false} maxWidth={null} edges={['top', 'bottom']}>
        <View style={styles.portal}>
          {/* Brand panel */}
          <View style={styles.brandPane}>
            <View style={styles.brandMotif} pointerEvents="none">
              <BeamMark size={420} tone="light" />
            </View>
            <Pressable accessibilityRole="link" onPress={() => router.push('/')}>
              <BeamLogo size={32} tone="light" />
            </Pressable>
            <View style={styles.brandCopy}>
              <Text variant="display" color="onPrimary">
                Welcome back.
              </Text>
              <Text variant="subhead" style={styles.brandSub}>
                Sign in to run your jobs, payments, and reporting from one place.
              </Text>
              <View style={styles.valueList}>
                {VALUE_PROPS.map((v) => (
                  <View key={v} style={styles.valueItem}>
                    <View style={styles.valueDot} />
                    <Text variant="body" color="onPrimary" style={styles.flex1}>
                      {v}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
            <Text variant="small" style={styles.brandSub}>
              getabeam.com
            </Text>
          </View>

          {/* Sign-in pane */}
          <View style={styles.signinPane}>
            <Card style={styles.signinCard}>
              <Text variant="title">Sign in</Text>
              <Text variant="small" color="secondary">
                Use the email and password for your Beam account.
              </Text>
              {form}
            </Card>
          </View>
        </View>
      </Screen>
    );
  }

  // --- Phone / native: centered single column (original) -----------------------------------
  return (
    <Screen>
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={styles.container}>
          <View style={styles.brand}>
            <BeamLogo size={40} />
            <Text variant="subhead" color="secondary">
              Sign in to your account
            </Text>
          </View>
          {form}
        </View>
      </KeyboardAvoidingView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  container: { flex: 1, justifyContent: 'center', gap: theme.spacing['4xl'] },
  brand: { alignItems: 'center', gap: theme.spacing.md },
  form: { gap: theme.spacing.md },
  submit: { marginTop: theme.spacing.sm },
  forgotRow: { alignSelf: 'flex-end', marginTop: -theme.spacing.xs },
  altRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: theme.spacing.sm, marginTop: theme.spacing.md },

  // two-pane portal
  portal: { flex: 1, flexDirection: 'row' },
  brandPane: {
    flex: 1,
    backgroundColor: theme.palette.indigo,
    padding: theme.spacing['4xl'],
    justifyContent: 'space-between',
    overflow: 'hidden',
  },
  brandMotif: { position: 'absolute', right: -100, bottom: -80, opacity: 0.08 },
  brandCopy: { gap: theme.spacing.lg, maxWidth: 460 },
  brandSub: { color: theme.palette.indigoWash },
  valueList: { gap: theme.spacing.md, marginTop: theme.spacing.sm },
  valueItem: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
  valueDot: { width: 7, height: 7, borderRadius: theme.radius.pill, backgroundColor: theme.palette.copper },
  flex1: { flex: 1 },
  signinPane: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: theme.spacing['4xl'] },
  signinCard: { width: '100%', maxWidth: 400, gap: theme.spacing.sm },
});
